import { FC, useState } from "react";
import {
    FormContainerStateReturnType,
    useAddMessage,
    useTrans,
} from "../../../hooks";
import { Text, Divider, Button, Box, useToast } from "@chakra-ui/react";
import { UploadApi, UserApi } from "../../../apis";
import { UnprocessableErrorResponse } from "../../../services/ApiPlatform/models";
import { useForm } from "react-hook-form";
import { AppFormContainer } from "../../../containers";
import { AppFileUploader, AppFormControl, AppIcon } from "../../../components";
import { APPC } from "../../../config";
import { MessageType } from "../../../atoms";

type ImportUserType = {
    importFileName: string;
};

export const ImportUserForm: FC<{
    formContainerState: FormContainerStateReturnType;
}> = ({ formContainerState }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { addMessage } = useAddMessage();

    // state & const
    const [loading, setLoading] = useState(false);
    const [importFileName, setImportFileName] = useState<File | undefined>(
        undefined
    );
    const [backendError, setBackendError] = useState("");

    const { handleSubmit } = useForm<ImportUserType>({
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = async (formData: ImportUserType) => {
        setLoading(true);
        if (importFileName) {
            const uploadFormData = new FormData();
            uploadFormData.set(
                "fileType",
                APPC.BE.Upload.FILETYPE.FILETYPE_USER_IMPORT
            );
            uploadFormData.set("file", importFileName);

            await UploadApi.upload<{ fileName: string }, FormData>(
                uploadFormData,
                undefined
            )
                .then(({ response, error, errorMessage }) => {
                    if (error instanceof UnprocessableErrorResponse) {
                        const { violations } = error;
                        Object.keys(violations).map((key) => {
                            setBackendError(violations[key]);
                        });
                    } else if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (response !== null) {
                        formData.importFileName = response.fileName;
                        UserApi.postImport<MessageType, ImportUserType>(
                            formData
                        )
                            .then(({ response, error, errorMessage }) => {
                                if (
                                    error instanceof UnprocessableErrorResponse
                                ) {
                                    const { violations } = error;
                                    Object.keys(violations).map((key) => {
                                        setBackendError(violations[key]);
                                    });
                                } else if (errorMessage) {
                                    toast({
                                        title: errorMessage,
                                        status: "error",
                                    });
                                } else if (response !== null) {
                                    addMessage({
                                        messageId: Number(response.messageId),
                                        type: response.type,
                                    });
                                    toast({
                                        title: t("cmn:message.save.success"),
                                        status: "success",
                                    });
                                    formContainerState.close();
                                }
                            })
                            .finally(() => setLoading(false));
                    }
                })
                .finally(() => setLoading(false));
        }
    };

    return (
        <AppFormContainer
            title={t("padm.ImportUserForm:text.pageTitle")}
            state={formContainerState}
            type={"modal"}
            submitLabel={t("padm.ImportUserForm:modal.submit.label")}
            size={"xxl"}
        >
            <>
                <form
                    id="app-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit(onSubmitHandler)();
                    }}
                >
                    <AppFormControl
                        isLoading={loading}
                        isInvalid={!!backendError}
                        message={t(backendError)}
                    >
                        <AppFileUploader
                            fileType={
                                APPC.BE.Upload.FILETYPE.FILETYPE_USER_IMPORT
                            }
                            onFileSelect={setImportFileName}
                            fileName={importFileName?.name}
                            iconName="icl-file-excel"
                            // errorMessage={backendError}
                        />
                    </AppFormControl>
                </form>
                <Divider
                    my={6}
                    borderColor={"var(--chakra-colors-primaryT80)"}
                />
                <Box mt={2} textAlign={"end"}>
                    <Button
                        as={"a"}
                        variant={"secondary"}
                        size={"sm"}
                        href="/templates/student-template.xlsx"
                    >
                        <AppIcon w="1rem" name="icl-document" />
                        <Text ml={4}>
                            {t(
                                "padm.ImportUserForm:button.downloadSampleTemplate"
                            )}
                        </Text>
                    </Button>
                </Box>
            </>
        </AppFormContainer>
    );
};
